/**
 * reconcile — hand ReactFlow the element objects it already has, wherever nothing about them changed.
 *
 * **Why a rebuilt element flickers.** ReactFlow v12 keeps each node's measured size and its handle
 * bounds in an internal record, and reuses that record only when the user node is the SAME OBJECT as
 * last time (`adoptUserNodes`, `checkEquality`). Any fresh object rebuilds the record from the user
 * node alone:
 *
 *     measured: { width: userNode.measured?.width, height: userNode.measured?.height },
 *     handleBounds: !userNode.measured ? undefined : previous.internals.handleBounds,
 *
 * A node we build from the store carries no `measured` — so the size is dropped, and because
 * `handleBounds` is reset whenever `measured` is absent, so are the anchor points every edge is drawn
 * between. The node renders unmeasured and its edges detach for a frame. That frame is the flicker,
 * and it costs the same whether the rebuild was caused by real news or by nothing at all.
 *
 * Our graphs are built from the store by pure functions, so "nothing at all" is the common case: the
 * mission store re-fetches on every session event and hands back fresh-but-equal arrays several times
 * a minute. Two rules follow, and this module is both of them:
 *
 *   - an element equal to the one on screen IS the one on screen — return the old object;
 *   - an element that really did change carries the old measurement across, so only the part that
 *     changed re-renders.
 *
 * Equality is compared two levels deep, which is exactly as deep as these elements go: primitives on
 * top, then `data` / `position` / `style` holding primitives and stable handler references. Anything
 * deeper (an edge's routed points, say) compares unequal and is treated as changed — the safe way to
 * be wrong, since it costs a re-render rather than a stale drawing.
 */

// What ReactFlow owns rather than us: written back into our state by `applyNodeChanges` after it has
// measured the DOM, and the reason a rebuilt node keeps its handle bounds.
const MEASURED_KEYS = ['measured', 'width', 'height'];

/** Equal one level down: same keys, each holding the same primitive or the same reference. */
function sameShallow(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(key => a[key] === b[key]);
}

/** Equal two levels down — the depth a built node or edge actually has. */
export function sameElement(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(key => sameShallow(a[key], b[key]));
}

/** The new element, wearing the measurement ReactFlow gave the old one. */
function withMeasurement(previous, next) {
  const carried = MEASURED_KEYS.filter(key => next[key] === undefined && previous[key] !== undefined);
  if (carried.length === 0) return next;
  const merged = { ...next };
  carried.forEach(key => { merged[key] = previous[key]; });
  return merged;
}

/**
 * Merge a freshly built element list onto the one already on screen.
 *
 * Returns `previous` itself when the two describe the same drawing, so a caller holding this in state
 * can skip the render entirely; otherwise a new array in which every unchanged element is the object
 * it already was.
 *
 * @param {Array} previous — what ReactFlow is currently showing
 * @param {Array} next     — what the store says it should show
 */
export function reconcileElements(previous, next) {
  const safeNext = next || [];
  if (!previous || previous.length === 0) return safeNext;

  const byId = new Map(previous.map(element => [element.id, element]));
  let identical = previous.length === safeNext.length;

  const merged = safeNext.map((element, index) => {
    const old = byId.get(element.id);
    if (!old) { identical = false; return element; }

    const carried = withMeasurement(old, element);
    if (sameElement(old, carried)) {
      if (previous[index] !== old) identical = false;  // same element, different place in the list
      return old;
    }
    identical = false;
    return carried;
  });

  return identical ? previous : merged;
}
